import axios, { AxiosInstance, AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import i18n from 'i18next';
import { ErrorApiDto } from '@/types/api';
import * as Sentry from '@sentry/react';
import { isPreviewMode } from '@/lib/previewMode';

interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  id?: string;
  _retry?: boolean;
  _retryCount?: number;
  _context?: string;
}

interface RetryConfig {
  maxRetries: number;
  retryDelay: number;
  backoffFactor: number;
}

export class ApiClient {
  private client: AxiosInstance;
  private language: string;
  private inflightGets = new Map<string, Promise<unknown>>();

  private readonly DEFAULT_RETRY_CONFIG: RetryConfig = {
    maxRetries: 3,
    retryDelay: 1500,
    backoffFactor: 2
  };

  constructor() {
    // Use the short language code (e.g. "en", "es")
    this.language = i18n.language.split('-')[0] ?? 'en';
    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    // Request interceptor.
    //
    // This used to prepend `/${language}` to every URL, turning `/public/forms/x` into
    // `/{lang}/public/forms/x` under the `/v1` baseURL. The server no longer has that segment: it
    // returns error codes this app translates itself, so the locale is not needed to render a
    // response (docs/locale-removal-plan.md, step 3). The two endpoints that genuinely still consume
    // the caller's locale pass it explicitly as `?lang=` — see `withLang` below. Prepending it to
    // every URL would now 404.
    this.client.interceptors.request.use(
      (config) => {
        if (config.url) {
          const extendedConfig = config as ExtendedAxiosRequestConfig;

          // Generate unique request ID for tracking
          extendedConfig.id = extendedConfig.id || `req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
          extendedConfig._retryCount = extendedConfig._retryCount || 0;

          // REMOVED: Sentry breadcrumb to prevent rate limit issues
          // Breadcrumbs are not needed for every request in public apps
        }
        return config;
      },
      (error) => {
        // Request setup errors are rare and usually indicate code issues
        // Don't capture to prevent infinite loops if Sentry itself fails
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor: handle errors consistently with smart retry logic
    this.client.interceptors.response.use(
      (response) => {
        // REMOVED: Sentry breadcrumb for successful responses to prevent rate limit issues
        // Success cases don't need tracking in public apps
        return response;
      },
      async (error: AxiosError<ErrorApiDto>) => {
        const config = error.config as ExtendedAxiosRequestConfig;
        const endpoint = this.extractEndpoint(config?.url);

        // REMOVED: Sentry breadcrumb for errors to prevent rate limit issues

        // Determine if error is retryable
        const isRetryable = this.isRetryableError(error);
        const retryCount = config?._retryCount || 0;

        // Smart Sentry capture logic: Only capture significant errors
        // Don't capture:
        // 1. 4xx client errors (user/validation errors, not bugs)
        // 2. 429 rate limit errors (prevents cascading failures)
        // 3. First attempt of retryable errors (reduces noise from transient issues)
        const status = error.response?.status;
        const shouldCapture =
          status !== 429 &&                                          // Never capture rate limits
          (!isRetryable || retryCount >= this.DEFAULT_RETRY_CONFIG.maxRetries) && // Wait until max retries
          (!status || status >= 500);                                // Only capture 5xx server errors

        if (shouldCapture) {
          Sentry.captureException(error, {
            contexts: {
              api: {
                endpoint,
                method: config?.method,
                url: config?.url,
                status: error.response?.status,
                statusText: error.response?.statusText,
                retryCount,
                isRetryable,
                requestId: config?.id,
                context: config?._context,
              },
            },
            // Use 'warning' for 4xx errors, 'error' for 5xx
            level: status && status >= 500 ? 'error' : 'warning',
          });
        }

        // Handle retryable errors
        if (isRetryable && this.shouldRetry(config)) {
          return this.retryRequest(config, error);
        }

        // Log error for debugging
        console.error(`API Error [${status}]:`, endpoint, error.response?.data || error.message);

        // Return formatted API error
        return Promise.reject(this.formatApiError(error));
      }
    );
  }

  /**
   * Determines if an error is retryable (transient network issues, 5xx errors)
   */
  private isRetryableError(error: AxiosError): boolean {
    // Never retry intentionally canceled/aborted requests
    if (error.code === 'ERR_CANCELED') {
      return false;
    }

    // Network errors (no response)
    if (!error.response) {
      return true;
    }

    // 5xx server errors are retryable
    const status = error.response.status;
    if (status >= 500 && status < 600) {
      return true;
    }

    // 408 Request Timeout and 429 Rate Limit are retryable
    return status === 408 || status === 429;


  }

  /**
   * Checks if request should be retried based on retry count
   */
  private shouldRetry(config: ExtendedAxiosRequestConfig): boolean {
    const retryCount = config._retryCount || 0;
    return retryCount < this.DEFAULT_RETRY_CONFIG.maxRetries;
  }

  /**
   * Retries a failed request with exponential backoff
   */
  private async retryRequest(config: ExtendedAxiosRequestConfig, _originalError: AxiosError): Promise<AxiosResponse> {
    const retryCount = (config._retryCount || 0) + 1;
    const delay = this.calculateRetryDelay(retryCount);

    console.info(
      `Retrying request (attempt ${retryCount}/${this.DEFAULT_RETRY_CONFIG.maxRetries}) after ${delay}ms:`,
      config.url
    );

    // Wait for retry delay with exponential backoff
    await new Promise(resolve => setTimeout(resolve, delay));

    // Update retry count
    config._retryCount = retryCount;

    try {
      return await this.client(config);
    } catch (retryError) {
      // If this was the last retry attempt, log it
      if (retryCount >= this.DEFAULT_RETRY_CONFIG.maxRetries) {
        console.error(
          `Request failed after ${retryCount} retries:`,
          config.url,
          config._context || ''
        );
      }
      throw retryError;
    }
  }

  /**
   * Calculates retry delay with exponential backoff
   * Attempt 1: 1s, Attempt 2: 2s, Attempt 3: 4s
   */
  private calculateRetryDelay(retryCount: number): number {
    return this.DEFAULT_RETRY_CONFIG.retryDelay *
      Math.pow(this.DEFAULT_RETRY_CONFIG.backoffFactor, retryCount - 1);
  }

  /**
   * Extracts clean endpoint from URL (removes base URL and query params)
   */
  private extractEndpoint(url?: string): string {
    if (!url) return '';
    const baseURL = import.meta.env.VITE_API_BASE_URL || '';
    return url.replace(baseURL, '').split('?')[0] ?? '';
  }

  /**
   * Formats axios error into consistent API error structure.
   * code=0 signals a true network failure (no response received) so callers
   * can distinguish offline/DNS/CORS from server-side 5xx errors.
   */
  /**
   * Normalizes any axios failure into {@link ErrorApiDto}.
   *
   * `code` is overwritten with the HTTP status on purpose — callers branch on it (e.g. 409 →
   * booking full). The server's own discriminator survives untouched in `errorCode`, and
   * `constraints` passes through so `services/api/validationErrors` can translate it. Spreading
   * `error.response.data` first is what keeps this tolerant of a pre-1.1.0 backend that sends only
   * `message`.
   */
  private formatApiError(error: AxiosError<ErrorApiDto>): ErrorApiDto {
    if (!error.response) {
      return {
        name: 'NETWORK_ERROR',
        code: 0,
        message: error.message || 'Network error',
        errorId: '',
      };
    }

    if (error.response.data) {
      return { ...error.response.data, code: error.response.status };
    }

    return {
      name: 'API_ERROR',
      code: error.response.status,
      message: error.message || 'An unexpected error occurred',
      errorId: '',
    };
  }

  // ==================== Public HTTP Methods ====================

  private getDeduplicationKey(url: string, params?: Record<string, unknown>): string {
    const paramStr = params ? JSON.stringify(params) : '';
    return `${url}|${paramStr}`;
  }

  /**
   * GET request with optional context for better error tracking.
   * Deduplicates identical in-flight GET requests — concurrent calls
   * to the same URL+params share a single network request.
   */
  public async get<T>(
    url: string,
    params?: Record<string, unknown>,
    context?: string,
    signal?: AbortSignal
  ): Promise<T> {
    // # WHY preview-mode short-circuit: PreviewDataProvider hydrates every store
    // slice the preview routes need; any GET that escapes signals a missing fixture.
    // Logging instead of throwing keeps the page rendering while making the gap visible.
    if (isPreviewMode()) {
      console.warn('[apiClient] preview-mode GET escaped — missing fixture?', url);
      return undefined as unknown as T;
    }
    // Skip deduplication when caller provides an AbortSignal (they need cancellation control)
    if (signal) {
      const config: ExtendedAxiosRequestConfig = { params, signal };
      if (context) config._context = context;
      const response = await this.client.get<T>(url, config);
      return response.data;
    }

    const key = this.getDeduplicationKey(url, params);
    const inflight = this.inflightGets.get(key);
    if (inflight) {
      return inflight as Promise<T>;
    }

    const config: ExtendedAxiosRequestConfig = { params };
    if (context) {
      config._context = context;
    }

    const promise = this.client.get<T>(url, config)
      .then(response => response.data)
      .finally(() => {
        this.inflightGets.delete(key);
      });

    this.inflightGets.set(key, promise);
    return promise;
  }

  /**
   * POST request with optional context for better error tracking
   */
  public async post<T>(
    url: string,
    data?: unknown,
    context?: string,
    signal?: AbortSignal
  ): Promise<T> {
    // # WHY: defense-in-depth — even with store-level guards, a stray POST from a
    // component that bypasses the store would mutate real data. Preview must be
    // strictly read-only from the user's perspective.
    if (isPreviewMode()) {
      console.warn('[apiClient] preview-mode POST suppressed', url);
      return undefined as unknown as T;
    }
    const config: ExtendedAxiosRequestConfig = {};
    if (context) {
      config._context = context;
    }
    if (signal) {
      config.signal = signal;
    }
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  /**
   * PUT request with optional context for better error tracking
   */
  public async put<T>(
    url: string,
    data?: unknown,
    context?: string
  ): Promise<T> {
    if (isPreviewMode()) {
      console.warn('[apiClient] preview-mode PUT suppressed', url);
      return undefined as unknown as T;
    }
    const config: ExtendedAxiosRequestConfig = {};
    if (context) {
      config._context = context;
    }
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  /**
   * DELETE request with optional context for better error tracking
   */
  public async delete<T>(
    url: string,
    context?: string
  ): Promise<T> {
    if (isPreviewMode()) {
      console.warn('[apiClient] preview-mode DELETE suppressed', url);
      return undefined as unknown as T;
    }
    const config: ExtendedAxiosRequestConfig = {};
    if (context) {
      config._context = context;
    }
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  /**
   * Updates the language for API requests
   */
  public updateLanguage(language: string) {
    this.language = language.split('-')[0] ?? 'en';
  }

  /**
   * Gets the current language header value
   */
  public getLanguageHeader(): string {
    return this.language;
  }

  /**
   * Appends `?lang=` to a URL for the few endpoints whose *output* depends on the guest's locale.
   *
   * <p>Use this ONLY where the locale changes what the server does — currently the two endpoints
   * that persist `submittedLocale` and decide which language the guest's confirmation email is
   * written in. Everywhere else the server is locale-invariant and this app translates error codes
   * locally, so adding `lang` would be noise that implies a dependency that does not exist.
   */
  public withLang(url: string): string {
    return `${url}${url.includes('?') ? '&' : '?'}lang=${encodeURIComponent(this.language)}`;
  }

  /**
   * Gets the axios instance for advanced use cases
   */
  public getClient(): AxiosInstance {
    return this.client;
  }
}

export const apiClient = new ApiClient();
